/**
 * État de la page intervenant qui doit survivre à un rechargement pour la même session : code
 * validé, matériel vérifié, présence sur scène, cam/micro voulus. Sans lui, chaque reload remet
 * l'invité au code, à la modale de préparation et à l'attente d'une nouvelle invitation.
 */
export type SpeakerSessionState = {
  codeValidated: boolean;
  setupDone: boolean;
  onStage: boolean;
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  savedAtMs: number;
};

export const EMPTY_SPEAKER_SESSION_STATE: SpeakerSessionState = {
  codeValidated: false,
  setupDone: false,
  onStage: false,
  cameraEnabled: true,
  microphoneEnabled: true,
  savedAtMs: 0,
};

/** Rythme auquel la page vivante rafraîchit son horodatage. */
export const SPEAKER_STATE_HEARTBEAT_MS = 30_000;

/**
 * L'horodatage dit « l'onglet était vivant à cet instant », pas « rien n'a changé depuis » : il est
 * rafraîchi tant que la page est au premier plan. Au-delà de cette fenêtre, la présence sur scène
 * n'est plus rejouée, car un retrait par la régie pendant que l'onglet dormait n'atteint jamais la
 * page (aucun état de scène n'est exposé par le back) et l'invité reviendrait à l'écran sans
 * invitation. Le code et la préparation, eux, ne périment pas.
 */
export const SPEAKER_STAGE_RESTORE_WINDOW_MS = 2 * 60_000;

/** Sous-ensemble de `Storage` utilisé, pour tester sans navigateur. */
export type SpeakerStateStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function speakerSessionStateKey(
  webinarId: number | null,
  sessionIdentifier: string | undefined,
): string | null {
  if (webinarId == null || !sessionIdentifier) return null;
  return `speaker-live:${webinarId}:${sessionIdentifier}`;
}

const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

/** Tolérant : une valeur absente, corrompue ou d'un autre format donne l'état vide. */
export function readSpeakerSessionState(
  store: SpeakerStateStore | null,
  key: string | null,
): SpeakerSessionState {
  if (!store || !key) return EMPTY_SPEAKER_SESSION_STATE;
  try {
    const raw = store.getItem(key);
    if (!raw) return EMPTY_SPEAKER_SESSION_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY_SPEAKER_SESSION_STATE;
    const record = parsed as Record<string, unknown>;
    return {
      codeValidated: isBoolean(record.codeValidated) ? record.codeValidated : false,
      setupDone: isBoolean(record.setupDone) ? record.setupDone : false,
      onStage: isBoolean(record.onStage) ? record.onStage : false,
      cameraEnabled: isBoolean(record.cameraEnabled) ? record.cameraEnabled : true,
      microphoneEnabled: isBoolean(record.microphoneEnabled) ? record.microphoneEnabled : true,
      savedAtMs: typeof record.savedAtMs === 'number' ? record.savedAtMs : 0,
    };
  } catch {
    return EMPTY_SPEAKER_SESSION_STATE;
  }
}

/** Reprise de la scène seulement si l'état est récent : au-delà, l'invité repasse en attente. */
export function canRestoreStage(state: SpeakerSessionState, nowMs: number): boolean {
  return state.onStage && nowMs - state.savedAtMs < SPEAKER_STAGE_RESTORE_WINDOW_MS;
}

export function writeSpeakerSessionState(
  store: SpeakerStateStore | null,
  key: string | null,
  state: SpeakerSessionState,
): void {
  if (!store || !key) return;
  try {
    store.setItem(key, JSON.stringify(state));
  } catch {
    // Stockage indisponible (navigation privée, quota) : la page fonctionne sans, comme avant.
  }
}

/** Battement de cœur : seul l'horodatage change, le reste de l'état est conservé tel quel. */
export function touchSpeakerSessionState(
  store: SpeakerStateStore | null,
  key: string | null,
  nowMs: number,
): void {
  if (!store || !key) return;
  const current = readSpeakerSessionState(store, key);
  if (current.savedAtMs === 0) return;
  writeSpeakerSessionState(store, key, { ...current, savedAtMs: nowMs });
}

export function clearSpeakerSessionState(
  store: SpeakerStateStore | null,
  key: string | null,
): void {
  if (!store || !key) return;
  try {
    store.removeItem(key);
  } catch {
    // idem
  }
}

/** `sessionStorage` du navigateur, ou null quand son accès lève (Safari privé, iframe bloquée). */
export function getSpeakerStateStore(): SpeakerStateStore | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}
